import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown, ShoppingBag, User } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";

const shopLinks = [
  { label: "All Products", to: "/shop" },
  { label: "Hoodies", to: "/shop?type=hoodies" },
  { label: "Tees", to: "/shop?type=tees" },
  { label: "Shorts", to: "/shop?type=shorts" },
  { label: "Bundles", to: "/shop?type=bundles" },
];

const dropsLinks = [
  { label: "All Drops", to: "/drops" },
  { label: "International Drops", to: "/drops?tab=international" },
  { label: "Domestic Drops", to: "/drops?tab=domestic" },
  { label: "Archive", to: "/drops?tab=archive" },
];

export const Navigation = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [shopOpen, setShopOpen] = useState(false);
  const [dropsOpen, setDropsOpen] = useState(false);
  const location = useLocation();
  const { count, setDrawerOpen } = useCart();
  const { user } = useAuth();
  const isHome = location.pathname === "/";

  useEffect(() => {
    const onScroll = () => {
      setScrolled(window.scrollY > window.innerHeight * 0.7);
    };
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  const solid = scrolled || !isHome;

  return (
    <>
      <nav
        className={`wc-nav fixed top-[36px] md:top-[34px] left-0 right-0 z-[90] ${
          solid ? "bg-black border-b border-white/20" : "bg-transparent"
        }`}
        data-testid="main-nav"
      >
        <div className="flex items-center justify-between px-6 md:px-10 py-5">
          <Link
            to="/"
            className="wc-heading text-white text-xl md:text-2xl tracking-[0.2em]"
            data-testid="nav-logo"
          >
            WRONGCONCEPTION
          </Link>

          {/* Desktop links */}
          <div className="hidden lg:flex items-center gap-10">
            <div
              className="relative"
              onMouseEnter={() => setShopOpen(true)}
              onMouseLeave={() => setShopOpen(false)}
            >
              <button className="wc-label text-white text-xs flex items-center gap-1" data-testid="nav-shop">
                Shop <ChevronDown size={14} />
              </button>
              {shopOpen && (
                <div className="absolute top-full left-0 pt-4">
                  <div className="bg-black border border-white/20 min-w-[220px]">
                    {shopLinks.map((l) => (
                      <Link
                        key={l.label}
                        to={l.to}
                        className="block wc-label text-xs text-white px-5 py-3 hover:bg-white hover:text-black"
                        data-testid={`nav-shop-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                      >
                        {l.label}
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div
              className="relative"
              onMouseEnter={() => setDropsOpen(true)}
              onMouseLeave={() => setDropsOpen(false)}
            >
              <button className="wc-label text-white text-xs flex items-center gap-1" data-testid="nav-drops">
                Drops <ChevronDown size={14} />
              </button>
              {dropsOpen && (
                <div className="absolute top-full left-0 pt-4">
                  <div className="bg-black border border-white/20 min-w-[240px]">
                    {dropsLinks.map((l) => (
                      <Link
                        key={l.label}
                        to={l.to}
                        className="block wc-label text-xs text-white px-5 py-3 hover:bg-white hover:text-black"
                        data-testid={`nav-drops-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                      >
                        {l.label}
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <Link to="/our-story" className="wc-label text-white text-xs" data-testid="nav-our-story">
              Our Story
            </Link>
            <Link to="/contact" className="wc-label text-white text-xs" data-testid="nav-contact">
              Contact
            </Link>

            <div className="flex items-center gap-5 pl-4 border-l border-white/20">
              <Link
                to={user ? "/account" : "/login"}
                className="text-white flex items-center gap-2 hover:opacity-70"
                data-testid="nav-account"
                aria-label="Account"
              >
                <User size={18} />
                <span className="wc-label text-[10px]">{user ? "ACCOUNT" : "LOG IN"}</span>
              </Link>
              <button
                onClick={() => setDrawerOpen(true)}
                className="text-white relative flex items-center gap-2 hover:opacity-70"
                data-testid="nav-cart"
                aria-label="Cart"
              >
                <ShoppingBag size={18} />
                <span className="wc-label text-[10px]">CART ({count})</span>
              </button>
            </div>
          </div>

          {/* Mobile right side: cart + hamburger */}
          <div className="flex items-center gap-4 lg:hidden">
            <button
              onClick={() => setDrawerOpen(true)}
              className="text-white relative"
              data-testid="nav-cart-mobile"
              aria-label="Cart"
            >
              <ShoppingBag size={22} />
              {count > 0 && (
                <span
                  className="absolute -top-2 -right-2 bg-white text-black text-[10px] wc-label px-1.5 py-0.5"
                  data-testid="nav-cart-count"
                >
                  {count}
                </span>
              )}
            </button>
            <button
              className="text-white"
              onClick={() => setOpen(true)}
              data-testid="nav-hamburger"
              aria-label="Open menu"
            >
              <Menu size={28} />
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile menu overlay */}
      <div
        className={`wc-mobile-menu ${open ? "open" : ""}`}
        data-testid="mobile-menu"
        aria-hidden={!open}
      >
        <button
          className="absolute top-7 right-6 text-white"
          onClick={() => setOpen(false)}
          data-testid="mobile-menu-close"
          aria-label="Close menu"
        >
          <X size={32} />
        </button>
        <div className="flex flex-col items-center gap-7 text-center">
          <Link to="/shop" className="wc-heading text-white text-4xl tracking-[0.1em]" data-testid="mobile-nav-shop">
            Shop
          </Link>
          <Link to="/drops" className="wc-heading text-white text-4xl tracking-[0.1em]" data-testid="mobile-nav-drops">
            Drops
          </Link>
          <Link to="/our-story" className="wc-heading text-white text-4xl tracking-[0.1em]" data-testid="mobile-nav-our-story">
            Our Story
          </Link>
          <Link to="/contact" className="wc-heading text-white text-4xl tracking-[0.1em]" data-testid="mobile-nav-contact">
            Contact
          </Link>
          <Link
            to={user ? "/account" : "/login"}
            className="wc-heading text-white text-3xl tracking-[0.1em] mt-4 border-t border-white/20 pt-6"
            data-testid="mobile-nav-account"
          >
            {user ? "Account" : "Log In"}
          </Link>
        </div>
      </div>
    </>
  );
};

export default Navigation;
